/**
 * Company · Labor rules (Part 7 spec §4).
 *
 * Pure functions — the period boundaries, the shop/field split, the overtime
 * rule and the attendance day classification. No React, no fetch.
 */

/** Spec §4 — the five QuickBooks-aligned windows. Workweek starts MONDAY. */
export type LaborPeriodKey = 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'ytd';

export const LABOR_PERIODS: readonly { key: LaborPeriodKey; label: string }[] = Object.freeze([
  { key: 'thisWeek',  label: 'This week to date' },
  { key: 'lastWeek',  label: 'Last week' },
  { key: 'thisMonth', label: 'This month to date' },
  { key: 'lastMonth', label: 'Last month' },
  { key: 'ytd',       label: 'Year to date' },
]);

/** Spec §4 — Lunch and the Service-Item card only exist on the WEEKLY views. */
export function isWeeklyPeriod(key: LaborPeriodKey): boolean {
  return key === 'thisWeek' || key === 'lastWeek';
}

/** Spec §4 — QuickBooks Time jobcode id for shop (non-job) time. */
export const SHOP_JOBCODE_ID = '3668547';

/** Spec §4 — the overtime threshold the payroll report applies, per ISO week. */
export const OT_THRESHOLD_HOURS = 40;

const DAY = 86_400_000;
export const NY_TZ = 'America/New_York';

/** ISO 'YYYY-MM-DD' for a local Date. */
export function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Spec §4 — the Monday that starts `d`'s workweek. */
export function startOfWorkWeek(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = out.getDay(); // 0 Sun … 6 Sat
  out.setDate(out.getDate() + (dow === 0 ? -6 : 1 - dow));
  return out;
}

/** Spec §4 — the inclusive [start, end] day bounds of a labor period. */
export function laborPeriodBounds(key: LaborPeriodKey, today: Date): { start: Date; end: Date } {
  const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const monday = startOfWorkWeek(midnight);
  switch (key) {
    case 'thisWeek': return { start: monday, end: midnight };
    case 'lastWeek': {
      const s = new Date(monday); s.setDate(s.getDate() - 7);
      const e = new Date(monday); e.setDate(e.getDate() - 1);
      return { start: s, end: e };
    }
    case 'thisMonth':
      return { start: new Date(midnight.getFullYear(), midnight.getMonth(), 1), end: midnight };
    case 'lastMonth':
      return {
        start: new Date(midnight.getFullYear(), midnight.getMonth() - 1, 1),
        end: new Date(midnight.getFullYear(), midnight.getMonth(), 0),
      };
    case 'ytd':
      return { start: new Date(midnight.getFullYear(), 0, 1), end: midnight };
  }
}

/** Every Mon–Fri date in [start, end], inclusive. */
export function workdaysBetween(start: Date, end: Date, cap = 400): Date[] {
  const out: Date[] = [];
  const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  while (d.getTime() <= end.getTime() && out.length < cap) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) out.push(new Date(d));
    d.setTime(d.getTime() + DAY);
  }
  return out;
}

/**
 * Spec §4 — overtime is decided PER ISO WEEK, never over the whole period:
 * hours over 40 in a week are OT, the rest are regular. (The previous version
 * summed raw hours across the period, which swept unpaid lunch and PTO into OT.)
 */
export function splitRegularOvertime(hoursByWeek: Iterable<number>): { regular: number; ot: number } {
  let regular = 0;
  let ot = 0;
  for (const h of hoursByWeek) {
    regular += Math.min(OT_THRESHOLD_HOURS, h);
    ot += Math.max(0, h - OT_THRESHOLD_HOURS);
  }
  return { regular, ot };
}

/** Spec §4 — `shopPct / fieldPct = hours ÷ hoursTotal × 100`. Zero total → 0/0. */
export function shopFieldSplit(shopHours: number, fieldHours: number): {
  total: number; shopPct: number; fieldPct: number;
} {
  const total = shopHours + fieldHours;
  if (total <= 0) return { total: 0, shopPct: 0, fieldPct: 0 };
  return { total, shopPct: (shopHours / total) * 100, fieldPct: (fieldHours / total) * 100 };
}

/** Spec §4 — `estCost = hoursTotal × user.pay_rate`. */
export function estimatedCost(hoursTotal: number, payRate: number | null): number {
  return payRate == null ? 0 : hoursTotal * payRate;
}

// ── Attendance (spec §4, `_attendanceSection`) ───────────────────────────────

export type AttendanceDay = 'present' | 'pto' | 'approved' | 'unapproved' | 'holiday';

export interface AttendanceDayInput {
  /** The day is a company holiday. Wins over everything. */
  isHoliday?: boolean;
  /** At least one clock-in that is NOT a PTO jobcode. */
  hasWorkedClockIn?: boolean;
  /** The M365 "Company PTO" calendar covers this person on this day. */
  onPtoCalendar?: boolean;
  /** A TSheets PTO jobcode was logged on this day. */
  hasPtoJobcode?: boolean;
}

/**
 * Spec §4 — classify one Mon–Fri workday for one employee. Branch order is the
 * rule: holiday → present → pto (calendar) → approved (PTO jobcode) →
 * unapproved.
 */
export function classifyAttendanceDay(d: AttendanceDayInput): AttendanceDay {
  if (d.isHoliday) return 'holiday';
  if (d.hasWorkedClockIn) return 'present';
  if (d.onPtoCalendar) return 'pto';
  if (d.hasPtoJobcode) return 'approved';
  return 'unapproved';
}

/**
 * Spec §4 — attendance scope is only ESTABLISHED employees: `hire_date` set AND
 * `pay_rate > 0`. Everyone else is returned flagged `salaried: true`, with
 * attendance metrics emitted as null and excluded from the company summary.
 */
export function isEstablishedEmployee(e: { hireDate?: string | null; payRate?: number | null }): boolean {
  return !!e.hireDate && (e.payRate ?? 0) > 0;
}

/** Spec §4 — attendance % = present days ÷ scheduled workdays × 100. */
export function attendancePct(present: number, scheduled: number): number | null {
  return scheduled > 0 ? (present / scheduled) * 100 : null;
}

/** Spec §4 — the attendance window starts at max(hireDate, Jan 1) and ends today. */
export function attendanceWindow(hireDate: string | null, today: Date): { start: Date; end: Date } {
  const jan1 = new Date(today.getFullYear(), 0, 1);
  const hire = hireDate ? new Date(hireDate) : null;
  const start = hire && Number.isFinite(hire.getTime()) && hire.getTime() > jan1.getTime()
    ? new Date(hire.getFullYear(), hire.getMonth(), hire.getDate())
    : jan1;
  return { start, end: new Date(today.getFullYear(), today.getMonth(), today.getDate()) };
}

/**
 * Spec §4 — `data/pto-allotments.json` is built offline from a manual
 * QuickBooks export because the Accounting API does not expose payroll PTO
 * balances. The one business rule baked into that script: Lance and Sue carry
 * over 64 h of PTO from the prior year.
 */
export const PTO_CARRYOVER_HOURS = 64;
export const PTO_CARRYOVER_NAMES: readonly string[] = Object.freeze(['Lance', 'Sue']);

/** Hours carried over into this year for one employee (spec §4). */
export function ptoCarryover(name: string): number {
  const first = (name ?? '').trim().split(/\s+/)[0] ?? '';
  return PTO_CARRYOVER_NAMES.some((n) => n.toLowerCase() === first.toLowerCase())
    ? PTO_CARRYOVER_HOURS
    : 0;
}
